import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import async from 'async';
import pluralize from 'pluralize';
import Header from '../../../components/Header/Header';
import { addNotification } from '../../../actions/notificationActions';
import DiagnosticsDialog from '../../../components/Devices/DiagnosticsDialog';
import ScreenshotDialog from '../../../components/Devices/ScreenshotDialog';
import getActiveCampaignId from '../../../components/CampaignSchedule/utils/getActiveCampaignId';
import { toggleFilters, closeFilters } from '../../../actions/filterActions';
import DevicesSearch from '../../../components/DevicesSearch/DevicesSearch';
import DevicesTable from '../../../components/DevicesTable/DevicesTable';
import DeviceActions from '../../../components/DeviceActions/DeviceActions';
import errorMessage from '../../../utils/errorMessage';
import UnregisterDeviceDialog from '../../../components/Devices/UnregisterDeviceDialog';
import DeployDialog from '../../../components/Devices/DeployDialog';
import RequestDiagnosticsDialog from '../../../components/Devices/RequestDiagnosticsDialog';
import RequestScreenshotDialog from '../../../components/Devices/RequestScreenshotDialog';
import RebootDeviceDialog from '../../../components/Devices/RebootDeviceDialog';
import RestartPlayerDialog from '../../../components/Devices/RestartPlayerDialog';
import ClearDataDialog from '../../../components/Devices/ClearDataDialog';
import UpgradeDeviceDialog from '../../../components/Devices/UpgradeDeviceDialog';
import CopyDeviceConfigDialog from '../../../components/Devices/CopyDeviceConfigDialog';
import {
  getDevices,
  getStatus,
  deployContent,
  restartPlayer,
  getVersions,
  rebootDevice,
  copyConfig,
  requestDiagnostics,
  requestScreenshot,
  upgradeAllDevices,
  upgradeDevice,
  clearData,
  unregisterDevice,
  overrideDownloadSchedule,
} from '../../../actions/deviceActions';
import { getHeartbeatPercent } from '../../../components/Devices/DeviceUtils';
import Pagination from '../../Pagination/Pagination';
import DeviceFilters from '../../../components/DeviceFilters/DeviceFilters';
import { getDeployer } from '../../../utils/deployHelper';
import RegisterDeviceDialog from '../../../components/Devices/RegisterDeviceDialog';
import OverrideDownloadDialog from '../../../components/Devices/OverrideDownloadDialog';
import './Devices.css';
import {
  clearDataCallbackHelper,
  mergeDevicesStatusHelper,
  rebootCallbackHelper,
  registerHelper,
  restartCallbackHelper,
  unregisterCallbackHelper,
  upgradeCallbackHelper,
  getUptimeButton,
  diagnosticsCallbackHelper,
  copyConfigCallbackHelper,
  screenshotCallbackHelper,
} from '../../../utils/v4/DevicesHelpers';
import Spinner from '../../../components/Spinner/Spinner';
import urlHelper from '../../../utils/urlHelper';

export function DevicesPage(props) {
  const [statusAndDevices, setStatusAndDevices] = useState([]);
  const [numFound, setNumFound] = useState(0);
  const [showScreenshotDialog, setShowScreenshotDialog] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState({});
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showUnregisterDialog, setShowUnregisterDialog] = useState(false);
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [showRequestDiagnosticsDialog, setShowRequestDiagnosticsDialog] = useState(false);
  const [showRequestScreenshotDialog, setShowRequestScreenshotDialog] = useState(false);
  const [showRebootDialog, setShowRebootDialog] = useState(false);
  const [showRestartDialog, setShowRestartDialog] = useState(false);
  const [showClearDataDialog, setShowClearDataDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showCopyConfigDialog, setShowCopyConfigDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showOverrideDownloadDialog, setShowOverrideDownloadDialog] = useState(false);
  const {
    auth,
    devices,
    users: { users },
    campaignSchedule: {
      campaignSchedule: { campaignScheduleEntries },
    },
    toggleFilters,
    deployContent,
  } = props;
  const { versions, copyConfig: copyConfigObj } = devices;
  const { downloadSchedule } = auth.user;
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showDiagnosticsDialog, setShowDiagnosticsDialog] = useState(false);
  const activeCampaignId = getActiveCampaignId(campaignScheduleEntries);
  const [shiftLastIndex, setShiftLastIndex] = useState(false);
  const {
    location: { search },
  } = props;
  const unselectAll = () => setSelectedDevices({});

  useEffect(() => {
    const { closeFilters, getVersions } = props;

    getVersions();

    return () => {
      closeFilters();
    };
  }, []);
  useEffect(() => {
    getDevices();
    setSelectedDevices({});
    setShiftLastIndex(false);
  }, [search]);
  useEffect(() => {
    mergeDevicesStatus(props);
  }, [devices]);

  const handleMultipleSelector = (event, devicesSelected, allDevices, setDevicesSelected) => {
    if (!event.shiftKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
      return;
    }

    event.preventDefault();
    const selectedDevicesArr = Object.values(devicesSelected);

    if (selectedDevicesArr.length === 0) {
      return;
    }

    const sortedDevicesArr = selectedDevicesArr.toSorted((a, b) => a.index - b.index);
    const indexOfSelectedItem = sortedDevicesArr[sortedDevicesArr.length - 1].index;

    if (event.key === 'ArrowDown') {
      const nextIndex = indexOfSelectedItem + 1;

      if (nextIndex >= allDevices.length) {
        return;
      }

      const nextDevice = allDevices[nextIndex];

      devicesSelected[nextIndex] = { ...nextDevice, index: nextIndex };
      setShiftLastIndex(nextIndex);
    } else if (event.key === 'ArrowUp') {
      delete devicesSelected[indexOfSelectedItem];
    }

    setDevicesSelected({ ...devicesSelected });
  };

  useEffect(() => {
    const handleKeyDown = event =>
      handleMultipleSelector(event, selectedDevices, statusAndDevices, setSelectedDevices);

    globalThis.addEventListener('keydown', handleKeyDown);

    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown);
    };
  }, [statusAndDevices, selectedDevices]);

  /**
   * Redirects page to include search query param
   * @param {string} searchValue
   */
  const executeSearch = searchValue => {
    const { history, location } = props;

    history.push(urlHelper.setUrlParam(location.search, 'search', searchValue.trim()));
  };

  const getDevices = () => {
    const {
      location: { search },
      getDevices,
    } = props;

    return getDevices(search);
  };

  const mergeDevicesStatus = nextProps => {
    mergeDevicesStatusHelper({
      nextProps,
      setNumFound,
      getHeartbeatPercent,
      setStatusAndDevices,
    });
  };

  const getSelectedItems = () => {
    return Object.values(selectedDevices);
  };

  const getSelectedExceptCopied = () => {
    const {
      devices: { copyConfig },
    } = props;

    if (!copyConfig || !statusAndDevices.length) {
      return [];
    }

    return getSelectedItems().filter(item => item.id !== copyConfig.id);
  };

  const localGetStatus = () => {
    const { Cypress } = globalThis;
    const { getStatus } = props;
    const ids = devices.length && devices.length <= 100 ? devices.map(item => item.id) : null;

    if (Cypress && !Cypress.env('RECORD') && !Cypress.cy.signstixCtx.serverInfo.routesStubbed) {
      return;
    }

    getStatus(ids);
  };

  const diagnosticsCallback = items => (err, results) => {
    const { addNotification } = props;

    diagnosticsCallbackHelper({
      results,
      getSelectedItems: () => items,
      addNotification,
      localGetStatus,
    });
  };

  const diagnostics = (items = getSelectedItems()) => {
    const { addNotification } = props;

    addNotification({ type: 'info', text: 'Requesting...' });

    async.filterSeries(
      items,
      (item, callback) =>
        requestDiagnostics(item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      diagnosticsCallback(items),
    );
  };

  const screenshot = (items = getSelectedItems()) => {
    const { addNotification } = props;

    addNotification({ type: 'info', text: 'Requesting...' });

    async.filterSeries(
      items,
      (item, callback) =>
        requestScreenshot(item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      screenshotCallback(items),
    );
  };

  const screenshotCallback = items => (err, results) => {
    const { addNotification } = props;

    screenshotCallbackHelper({
      results,
      addNotification,
      localGetStatus,
      getSelectedItems: () => items,
    });
  };

  const handleDiagnosticsClicked = device => {
    setSelectedDevice(device);
    setShowDiagnosticsDialog(true);
  };

  const handleScreenshotClicked = device => {
    setSelectedDevice(device);
    setShowScreenshotDialog(true);
  };

  const handleDeviceSelectedChange = (event, device, index) => {
    let devicesSelected = { ...selectedDevices };

    globalThis.getSelection().removeAllRanges();

    if (event.shiftKey && shiftLastIndex !== false) {
      const pre = shiftLastIndex;
      const start = Math.min(pre, index);
      const limit = Math.max(pre, index);

      for (let pos = start; pos <= limit; pos += 1) {
        devicesSelected[pos] = { ...statusAndDevices[pos], index: pos };
      }

      setSelectedDevices(devicesSelected);

      return;
    }

    const isMac = navigator.platform.includes('Mac');

    if (event[isMac ? 'metaKey' : 'ctrlKey']) {
      if (devicesSelected[index]) {
        delete devicesSelected[index];
        setShiftLastIndex(false);
      } else {
        devicesSelected[index] = { ...device, index };
        setShiftLastIndex(index);
      }

      setSelectedDevices(devicesSelected);

      return;
    }

    if (!devicesSelected[index]) {
      devicesSelected = {};
      devicesSelected[index] = { ...device, index };
      setShiftLastIndex(index);
    }

    setSelectedDevices(devicesSelected);
  };

  const toggleMoreFilters = () => {
    setShowMoreFilters(!showMoreFilters);
  };

  const successUnregisterDialog = () => {
    setShowUnregisterDialog(false);
    unregister();
  };

  const unregister = () => {
    const items = getSelectedItems();

    async.filter(
      items,
      (item, callback) =>
        unregisterDevice(item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      unregisterCallback,
    );
  };

  const unregisterCallback = (err, results) => {
    const { addNotification } = props;

    unregisterCallbackHelper({
      results,
      getSelectedItems,
      addNotification,
      unselectAll,
      getDevices,
    });
  };

  const successRequestDiagnosticsDialog = () => {
    setShowRequestDiagnosticsDialog(false);
    diagnostics();
  };

  const successRequestScreenshotDialog = () => {
    setShowRequestScreenshotDialog(false);
    screenshot();
  };

  const reboot = () => {
    const items = getSelectedItems();

    async.filter(
      items,
      (item, callback) =>
        rebootDevice(item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      rebootCallback,
    );
  };

  const rebootCallback = (err, results) => {
    const { addNotification } = props;

    rebootCallbackHelper({
      results,
      addNotification,
      getSelectedItems,
    });
  };

  const successRebootDialog = () => {
    setShowRebootDialog(false);
    reboot();
  };

  const restart = version => {
    const items = getSelectedItems();

    async.filter(
      items,
      (item, callback) =>
        restartPlayer(item.id, version).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      restartCallback,
    );
  };

  const restartCallback = (err, results) => {
    const { addNotification } = props;

    restartCallbackHelper({
      results,
      addNotification,
      getSelectedItems,
    });
  };

  const successRestartDialog = () => {
    setShowRestartDialog(false);
    restart();
  };

  const successClearDataDialog = () => {
    setShowClearDataDialog(false);
    clearDataLoc();
  };

  const clearDataLoc = () => {
    const items = getSelectedItems();

    async.filter(
      items,
      (item, callback) =>
        clearData(item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      clearDataCallback,
    );
  };

  const clearDataCallback = (err, results) => {
    const { addNotification } = props;

    clearDataCallbackHelper({
      results,
      getSelectedItems,
      addNotification,
      unselectAll,
      getDevices,
    });
  };

  const upgradeAll = version => {
    const { addNotification } = props;

    setShowUpgradeDialog(false);

    return upgradeAllDevices(version).then(
      () => addNotification({ type: 'success', text: 'All Devices requested to update.' }),
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  const upgrade = version => {
    const items = getSelectedItems();

    setShowUpgradeDialog(false);
    async.filter(
      items,
      (item, callback) =>
        upgradeDevice(item.id, version).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      upgradeCallback,
    );
  };

  const upgradeCallback = (err, results) => {
    const { addNotification } = props;

    upgradeCallbackHelper({
      results,
      addNotification,
      getSelectedItems,
    });
  };

  const successCopyConfigDialog = () => {
    setShowCopyConfigDialog(false);
    copyConfigLoc();
  };

  const copyConfigLoc = () => {
    const {
      devices: {
        copyConfig: { id },
      },
    } = props;
    const items = getSelectedExceptCopied();

    async.filter(
      items,
      (item, callback) =>
        copyConfig(id, item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      copyConfigCallback,
    );
  };

  const copyConfigCallback = (err, results) => {
    const { addNotification } = props;

    copyConfigCallbackHelper({
      results,
      getSelectedExceptCopied,
      addNotification,
      unselectAll,
      getDevices,
    });
  };

  const register = (id, description) => {
    const { history, addNotification } = props;

    return registerHelper({
      id,
      description,
      history,
      addNotification,
      setShowRegisterDialog,
    });
  };

  const goToDevicesPage = id => {
    const { history } = props;

    history.push(`/devices/${id}`);
  };

  const successOverrideDownloadDialog = () => {
    setShowOverrideDownloadDialog(false);
    const items = getSelectedItems();

    async.filter(
      items,
      (item, callback) =>
        overrideDownloadSchedule(item.id).then(
          res => callback(null, res),
          () => callback(null, false),
        ),
      overrideDownloadScheduleCallback,
    );
  };

  /**
   * @param {Error|null|undefined} err
   * @param {{description: string}[]} results
   */
  const overrideDownloadScheduleCallback = (err, results) => {
    const { addNotification } = props;
    const items = getSelectedItems();
    let failedItems;
    let errorMsg = '';

    if (items.length === results.length) {
      addNotification({
        type: 'success',
        text: `${pluralize('Override Download Schedule', items.length, true)} set.`,
      });
    } else {
      // collect the failed request items
      failedItems = items.filter(item => !results.includes(item));
      failedItems.forEach(item => {
        errorMsg += (errorMsg ? ', ' : 'Override Download Schedule failed: ') + item.description;
      });
      addNotification({ type: 'danger', text: errorMsg });
    }
  };

  const loaded = statusAndDevices?.length >= 0;

  return (
    <div className="bg-[#EFF5F8] min-h-screen">
      <Helmet title="Devices" />
      <DeviceFilters toggleMoreFilters={toggleMoreFilters} showMoreFilters={showMoreFilters} />
      <div className="page-content !bg-transparent">
        <Header />
        <Spinner loading={!loaded}>
          {/* ICON SELECTOR SECTION */}
          <DeviceActions
            selectedDevices={selectedDevices}
            setShowUnregisterDialog={setShowUnregisterDialog}
            setShowDeployDialog={setShowDeployDialog}
            setShowRequestDiagnosticsDialog={setShowRequestDiagnosticsDialog}
            setShowRequestScreenshotDialog={setShowRequestScreenshotDialog}
            setShowRebootDialog={setShowRebootDialog}
            setShowRestartDialog={setShowRestartDialog}
            setShowClearDataDialog={setShowClearDataDialog}
            setShowUpgradeDialog={setShowUpgradeDialog}
            setShowCopyConfigDialog={setShowCopyConfigDialog}
            setShowRegisterDialog={setShowRegisterDialog}
            setShowOverrideDownloadSchedule={setShowOverrideDownloadDialog}
          />
          {/* SEARCH SECTION */}
          <DevicesSearch executeSearch={executeSearch} toggleFilters={toggleFilters} />
          {/* TABLE OF Devices */}
          <DevicesTable
            auth={auth}
            statusAndDevices={statusAndDevices}
            getUptimeButton={getUptimeButton}
            handleDiagnosticsClicked={handleDiagnosticsClicked}
            handleScreenshotClicked={handleScreenshotClicked}
            handleDeviceSelectedChange={handleDeviceSelectedChange}
            activeCampaignId={activeCampaignId}
            goToDevicesPage={goToDevicesPage}
            selectedDevices={selectedDevices}
          />

          {/* PAGINATION */}
          <Pagination itemsFound={numFound} />
        </Spinner>
      </div>

      <DiagnosticsDialog
        auth={auth}
        users={users}
        device={selectedDevice}
        show={showDiagnosticsDialog}
        onHide={() => setShowDiagnosticsDialog(false)}
        onSuccess={() => setShowDiagnosticsDialog(false)}
        onRequestDiagnostics={() => diagnostics([selectedDevice])}
      />
      <ScreenshotDialog
        auth={auth}
        users={users}
        device={selectedDevice}
        show={showScreenshotDialog}
        onHide={() => setShowScreenshotDialog(false)}
        onSuccess={() => setShowScreenshotDialog(false)}
        onRequestScreenshot={() => screenshot([selectedDevice])}
      />
      <UnregisterDeviceDialog
        devices={Object.values(selectedDevices)}
        show={showUnregisterDialog}
        onHide={() => setShowUnregisterDialog(false)}
        onSuccess={successUnregisterDialog}
      />
      <DeployDialog
        show={showDeployDialog}
        onHide={() => setShowDeployDialog(false)}
        deployer={getDeployer(deployContent)}
        downloadSchedule={downloadSchedule}
        auth={auth}
        devices={Object.values(selectedDevices)}
      />
      <RequestDiagnosticsDialog
        devices={Object.values(selectedDevices)}
        show={showRequestDiagnosticsDialog}
        onHide={() => setShowRequestDiagnosticsDialog(false)}
        onSuccess={successRequestDiagnosticsDialog}
      />
      <RequestScreenshotDialog
        devices={Object.values(selectedDevices)}
        show={showRequestScreenshotDialog}
        onHide={() => setShowRequestScreenshotDialog(false)}
        onSuccess={successRequestScreenshotDialog}
      />
      <RebootDeviceDialog
        devices={Object.values(selectedDevices)}
        show={showRebootDialog}
        onHide={() => setShowRebootDialog(false)}
        onSuccess={successRebootDialog}
      />
      <RestartPlayerDialog
        devices={Object.values(selectedDevices)}
        show={showRestartDialog}
        onHide={() => setShowRestartDialog(false)}
        onSuccess={successRestartDialog}
      />
      <ClearDataDialog
        devices={Object.values(selectedDevices)}
        show={showClearDataDialog}
        onHide={() => setShowClearDataDialog(false)}
        onSuccess={successClearDataDialog}
      />
      <UpgradeDeviceDialog
        devices={Object.values(selectedDevices)}
        versions={versions}
        show={showUpgradeDialog}
        onHide={() => setShowUpgradeDialog(false)}
        onUpgrade={upgrade}
        onUpgradeAll={upgradeAll}
      />
      <CopyDeviceConfigDialog
        devices={getSelectedExceptCopied()}
        copyConfig={copyConfigObj}
        show={showCopyConfigDialog}
        onHide={() => setShowCopyConfigDialog(false)}
        onSuccess={successCopyConfigDialog}
      />

      <RegisterDeviceDialog
        show={showRegisterDialog}
        onHide={() => setShowRegisterDialog(false)}
        onSuccess={register}
      />

      <OverrideDownloadDialog
        show={showOverrideDownloadDialog}
        onHide={() => setShowOverrideDownloadDialog(false)}
        devices={Object.values(selectedDevices)}
        onSuccess={successOverrideDownloadDialog}
      />
    </div>
  );
}

DevicesPage.propTypes = {
  auth: PropTypes.object.isRequired,
  users: PropTypes.object.isRequired,
  location: PropTypes.object,
  getDevices: PropTypes.func.isRequired,
  getStatus: PropTypes.func.isRequired,
  devices: PropTypes.object.isRequired,
  addNotification: PropTypes.func.isRequired,
  campaignSchedule: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  deployContent: PropTypes.func.isRequired,
  closeFilters: PropTypes.func.isRequired,
  getVersions: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  devices: state.devices,
  auth: state.auth,
  users: state.users,
  campaignSchedule: state.campaignSchedule,
});
const mapDispatchToProps = {
  getDevices,
  addNotification,
  getStatus,
  toggleFilters,
  deployContent,
  closeFilters,
  getVersions,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DevicesPage));
